/**
 * @pitbrain/shared - Core Domain Library
 *
 * Shared business logic for SmartRoadRace platform
 * Platform-agnostic domain models and utilities
 */

// ============================================================================
// Domain Models - Core business entities
// ============================================================================

// Telemetry - Raw sensor data
export * from './domain/telemetry/raw/GpsReading'
export * from './domain/telemetry/raw/GyroscopeReading'
export * from './domain/telemetry/raw/AccelerometerReading'
export * from './domain/telemetry/raw/RawTelemetryData'

// Telemetry - Processed data with derived metrics
export * from './domain/telemetry/processed/GpsPoint'
export * from './domain/telemetry/processed/LeanAngle'
export * from './domain/telemetry/processed/Acceleration'
export * from './domain/telemetry/processed/GForce'
export * from './domain/telemetry/processed/TelemetryStatistics'
export * from './domain/telemetry/processed/ProcessedTelemetryData'
export * from './domain/telemetry/processed/TelemetryDiagnostics'

// Telemetry - Indexing for efficient lookups
export * from './domain/telemetry/indexing/TelemetryIndex'
export * from './domain/telemetry/indexing/IndexedTelemetry'

// Telemetry - Spatial analysis
export * from './domain/telemetry/spatial/SpatialBucket'

// Telemetry - Type aliases for backward compatibility
export type { GpsPoint as ProcessedGpsPoint } from './domain/telemetry/processed/GpsPoint'

// Lap - Individual lap data and analysis
export * from './domain/lap/LapBounds'
export * from './domain/lap/LapMetadata'
export * from './domain/lap/Lap'
export * from './domain/lap/LapPoint'
export * from './domain/lap/lapAnalysis'
export * from './domain/lap/lapProcessingVersion'
export * from './domain/lap/TimeDriftCalculator'

// Track Session - Collection of laps
export * from './domain/track-session/TrackSessionMetadata'
export * from './domain/track-session/TrackSessionStatus'
export * from './domain/track-session/TrackSession'

// Track - Track configuration and metadata
export * from './domain/track/TrackLocation'
export * from './domain/track/FinishLine'
export * from './domain/track/TrackMetadata'
// Note: Domain Track model not exported to avoid conflict with TrackDto
// export * from './domain/track/Track'

// ============================================================================
// Utils - Pure utility functions
// ============================================================================

export * from './utils/distanceUtils'
export * from './utils/geometryUtils'
export * from './utils/guidUtils'
export * from './utils/timeUtils'
export * from './utils/telemetryUtils'
export * from './utils/sustainedPeak'

// ============================================================================
// Processors - Data transformation and analysis
// ============================================================================

export {
  createMultiResolution,
  decimateTelemetryByDistance,
  createLegacyIndexedTelemetry,
} from './processors/telemetryProcessor'
export * from './processors/chartDataProcessor'
export * from './processors/rawTelemetryProcessor'
export { applyGpsDerivedTier } from './processors/gpsDerivedTierRelabel'
export {
  classifyImuVibration,
  reduceAccelerometerToGpsRate,
} from './processors/gpsRateAccelReduction'
export * from './processors/lapComparison'
export * from './processors/pipeline'
export { createSpatialBuckets } from './processors/spatial/spatialProcessing'
export {
  gpsSpeedTrendMs2,
  validateAccelConsistency,
} from './processors/accelConsistencyValidator'

// Coasting Detection - DSP pipeline for coasting analysis
// Main pipeline function
export { executeCoastingPipeline } from './processors/coasting'
// Helper functions
export {
  buildCoastingMap,
  getCoastingAtDistance,
  collectAndClassifyCoastingData,
  calculateCoastingIntensity,
  calculateCombinedGForce,
  DEFAULT_COASTING_CONFIG,
} from './processors/coasting'
// Vehicle strategies
export {
  createVehicleStrategy,
  getSupportedVehicleTypes,
  isValidVehicleType,
  DEFAULT_VEHICLE_TYPE,
  MotorcycleStrategy,
  CarStrategy,
  GoKartStrategy,
} from './processors/coasting'
// Types
export type {
  CoastingPeriod,
  CoastingConfig,
  CoastingStatistics,
  VehicleType,
  VehicleStrategy,
  VehiclePhysicsConfig,
  TransitionTiming,
  TransitionSpeed,
  TransitionAnalysis,
} from './processors/coasting'

// ============================================================================
// Types - Legacy compatibility types for existing web app code
// ============================================================================

export * from './types/TelemetryTypes'
export type { IGpsPointProvider } from './types/GpsPoint'
export * from './types/LatLng'

// ============================================================================
// API Types - DTOs and request/response types for backend communication
// ============================================================================

export type {
  PzRacingDataRow,
  I2mDataRow,
  AimDataRow,
  CsvDataRow,
  Coordinate,
  UpsertLapRequest,
  LapDto,
  Weather,
  CreateTrackSessionRequest,
  DriverDto,
  CreateVehicleRequest,
  VehicleDto,
  VehicleEngineDto,
  TrackSessionDto,
  TrackSessionMetadataDto,
  LatLngDto,
  Speed,
  Device,
  TelemetryDto,
  TelemetryDiagnosticsDto,
  Gps,
  Gyroscope,
  Accelerometer,
  Direction,
  DeviceTime,
  GyroReading,
  AccelerationReading,
  TrackDto,
  UserSuggestedTrack,
  NearbyTrackResult,
  TrackChangeResult,
  Guid,
} from './api/apiTypes'

export {
  TelemetryType,
  UserSuggestedTrackStatus,
  WeatherCondition,
  guid,
} from './api/apiTypes'

// Backward compatibility: Track refers to TrackDto (API contract)
export type { TrackDto as Track } from './api/apiTypes'

// ============================================================================
// API Mappers - Conversion between domain models and DTOs
// ============================================================================

export { rawTelemetryToDto } from './api/mappers/telemetryMapper'
export { telemetryDtoToRaw } from './api/mappers/telemetryMapper'
export { sessionDiagnosticsToDto } from './api/mappers/telemetryMapper'
export {
  lapToDto,
  lapDtoToLap,
  lapTelemetryToDto,
} from './api/mappers/lapMapper'

// ============================================================================
// Interfaces
// ============================================================================

export * from './interfaces/IChartCache'

// ============================================================================
// Analysis - Core metric types and pure algorithms
// ============================================================================

// Core metric type (TelemetryMetric, ALL_METRICS, isValidMetric)
export * from './analysis/metrics/TelemetryMetricTypes'

// Color interpolation algorithm (pure math - hexToRgb, rgbToHex, interpolateGradientColor)
export * from './analysis/visualization/GradientColorScales'

// Gradient display primitives (types used by variance heatmap + track gradient)
export type {
  GradientColorScale,
  GradientPoint,
} from './analysis/visualization/gradientTypes'

// Variance color scale - still used by the trackGradient-based heatmap legend
export { VARIANCE_COLOR_SCALE } from './analysis/variance/types'
export {
  extractMetricFromPreprocessed,
  normalizeTimeDrift,
} from './analysis/variance/metricExtractors'
export {
  computeAutoExcludedLapIds,
  type OutlierLapInput,
} from './analysis/sessionAnalysis/lapOutlierExclusion'
export {
  buildLapTimeDeltas,
  type LapTimeDelta,
} from './analysis/sessionAnalysis/lapTimeDeltas'
export {
  getKeyLapIds,
  getDisplayLaps,
  getLapSortValue,
} from './analysis/sessionAnalysis/keyLapSelection'
export {
  getZoneMarkerKind,
  type ZoneMarkerKind,
} from './analysis/sessionAnalysis/zoneMarkerKind'
export {
  runSessionAnalysis,
  filterExcludedLaps,
  detectSessionZones,
  type SessionAnalysisOptions,
  type SessionAnalysisResult,
} from './analysis/sessionAnalysis/analyzeSession'
export { selectStoredLaps } from './analysis/sessionAnalysis/lapSelection'

// Session-analysis tuning config (pass 24)
export * from './analysis/config'
export * from './analysis/config/leanProcessingVersion'

// Telemetry chart - brake/throttle % derivation + zone chart series
export {
  buildZoneChartSeries,
  zoneSeriesGatedLapIds,
  zoneSeriesGatedLapReasons,
  zoneChartMetric,
  zoneChartMetricOptions,
  fitSpeedAxisBounds,
  ZONE_CHART_METRIC,
  ZONE_CHART_METRIC_LABELS,
  BRAKE_METRIC_CAPTION,
  CHART_GUIDE_TITLE,
  CHART_MARKER_CAPTION,
  type ZoneChartMetric,
  type ZoneChartPoint,
  type ZoneChartSeries,
  type BuildZoneChartSeriesOptions,
} from './analysis/telemetryChart/zoneChartSeries'
export {
  buildMedianSpeedReference,
  speedDeltaAt,
  speedDeltaAxisBound,
  SPEED_DELTA_AXIS_LABEL,
  SPEED_DELTA_ZERO_LABEL,
  SPEED_DELTA_CAPTION,
  SPEED_DELTA_GUIDE_KMH,
  SPEED_DELTA_GUIDE_LABELS,
  SPEED_DELTA_GUIDE_CAPTION,
  type SpeedSample,
  type SpeedReference,
} from './analysis/telemetryChart/speedDeltaSeries'
export {
  buildSignedAccelDisplay,
  buildSignedAccelReference,
  maxAccelGAtSpeed,
  lapGateReason,
  describeGatedLaps,
  SIGNED_ACCEL_SPEED_BIN_KMH,
  SIGNED_BRAKE_G_CAP,
  SIGNED_ACCEL_G_CAP,
  SIGNED_MIN_G_REFERENCE,
  ACCEL_BAND_FRACTION,
  SIGNED_ACCEL_FILL_MAX_LAPS,
  type SignedAccelSourcePoint,
  type SignedAccelLapInput,
  type SignedAccelReference,
  type SignedAccelGateReason,
  type SignedAccelPoint,
  type SignedAccelLap,
  type SignedAccelDisplay,
} from './analysis/telemetryChart/signedAccelDisplay'

// Zone display - chart window refinement (turn-in)
export {
  refineZoneBoundaries,
  findTurnInDistance,
  findCorneringDisplayStart,
  computeZoneDisplayBounds,
} from './analysis/sessionAnalysis/zoneDisplay/zoneDisplayBounds'
export {
  buildRecalibratedLapPoints,
  type RecalibratedLap,
} from './analysis/sessionAnalysis/zoneDisplay/recalibratedLapPoints'
export {
  speedToleranceKmh,
  distanceToleranceM,
  type ConsistencyDisplayBounds,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyThresholds'
export {
  buildPercentRows,
  composeZoneConsistencyPercent,
  focusConsistentMessage,
  pctBand,
  zonePctBand,
  formatPercentMarkerLabel,
  timeToleranceMs,
  type PercentMetricKey,
  type PercentBand,
  type PercentRowNature,
  type ConsistencyPercentRow,
  type PercentOutlier,
  type PercentOffLap,
  type PercentRowStats,
  type PercentMarker,
  type ZoneConsistencyPercent,
  type ZoneOutlierAttribution,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyPercent'
export {
  buildCurvePercentRow,
  buildZoneConsistencyPercent,
  type BuildCurvePercentRowArgs,
  type BuildZoneConsistencyPercentArgs,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/curvePercent'
export { binMedianSpeeds } from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/binSpeedSpreads'
export {
  tracesFromRecalibratedLaps,
  tracesFromRawLaps,
  tracesFromChartTelemetry,
  type SpeedTrace,
  type SpeedTracePoint,
  type ChartTelemetryLapLike,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/speedTraces'
export {
  FLAT_VALLEY_MIN_WIDTH_M,
  computeValleyWidthM,
  flatValleyNote,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/flatValley'
export { formatZoneTimeDelta } from './analysis/sessionAnalysis/zoneDisplay/timeDelta'
export { lapTableFootnote } from './analysis/sessionAnalysis/zoneDisplay/lapTableFootnote'
export {
  fastestZoneLapId,
  slowestZoneLapId,
} from './analysis/sessionAnalysis/zoneDisplay/lapExtremes'
export {
  buildConsistencyBand,
  CONSISTENCY_BAND_CAPTION,
  type ConsistencyBand,
  type BuildConsistencyBandArgs,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyBand'
export {
  buildConsistencyGradient,
  type BuildConsistencyGradientArgs,
  type ZoneVariancePoint,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyGradient'
export {
  buildTrackConsistencyGradient,
  type TrackGradientZoneWindow,
} from './analysis/sessionAnalysis/zoneDisplay/referencePointConsistency/trackConsistencyGradient'

// Lap Collection Summary - aggregate stats for multi-lap/multi-rider views
export { summarizeLapCollection } from './analysis/lapCollectionSummary'
export type { LapCollectionSummary } from './analysis/lapCollectionSummary'

// Weather formatting - platform-agnostic labels and colors
export {
  getWeatherLabel,
  getWeatherColor,
  toWeatherCondition,
} from './domain/weather/weatherFormatting'

// Track utilities - layout comparison and finish line geometry
export {
  getFinishLinePosition,
  areTracksCompatible,
} from './domain/track/trackUtils'

// Track Zone Analysis - zone detection and variance aggregation
export {
  analyzeTrackZones,
  sessionConsistencyPercent,
  sessionConsistencySummary,
  isSolidZone,
  identifyFocusZones,
  identifyImproveZones,
} from './analysis/zones/TrackZoneAnalysis'
export type { SessionConsistencySummary } from './analysis/zones/TrackZoneAnalysis'
export {
  detectZoneBoundaries,
  extractZoneData,
} from './analysis/zones/ZoneDetection'
export { G_CONVERSION } from './analysis/zones/LapZoneMetrics'
export { hasReliableBrakeRelease } from './analysis/zones/ZoneLapData'
export type {
  ZoneBoundary,
  ZoneDataPoint,
} from './analysis/zones/ZoneDetection'
export type {
  TrackZone,
  TrackZoneType,
  ZoneAnalysisResult,
  ZoneLapData,
  ZoneConsensusMetrics,
  ZoneVarianceMetrics,
  ZoneImpact,
  ZoneDetectionConfig,
  ZoneSeverity,
  InconsistencyType,
} from './analysis/zones/TrackZoneTypes'

// --- GoPro GPMF ---
// The format knowledge; the container demuxing stays platform-side because
// this package carries no runtime dependencies.
export type {
  GpmfPayload,
  GpmfGpsSample,
  GpmfXyzSample,
  GpmfStreams,
} from './devices/gopro'
export { parseGpmfPayloads, parseGpmfBuffer } from './devices/gopro'

// --- GPX ---
export { parseGpx } from './devices/gpx'
